import { FlagIcon } from '@/components/icons/FlagIcon';
import { Groups } from '@/components/icons/groups';
import { getDisplayName } from '@/hooks/groups/useGroupsAndMembers';
import { UserAvatar } from '@/pages/groups/UserAvatar';
import { getStringValue, safeFormatDate } from '@/shared/utils/helpers';
// import { SandClock } from '@/components/icons/sandClock';
// import { OnPlanIcon } from '@/components/icons/onPlanIcon/OnPlanIcon';
// import { ChevronRightIcon } from 'lucide-react';
// import { StatusCircle } from '@/components/icons/statusCircle';
// import { Pause } from '@/components/icons/pauseIcon';
// import { VideoCountIcon } from '@/components/icons/videoCount';
// import { ImagesCountIcon } from '@/components/icons/ImagesCount';
// import Button from '@/shared/ui/Button';
// import { formatTimeHMS } from '@/utils/time';
// import { getTaskTimeStatus } from '@/features/tasks/mobile-calendar/utils/taskStatus';

interface ActiveTaskCardProps {
  task: ANY;
  elapsedMs: number;
  plannedMs: number;
  assignee?: ANY;
  createdBy?: ANY;
  previewImage?: {
    downloadUrl: string;
    name?: string;
  };
  isActive?: boolean;
  onPause?: () => void;
  onComplete?: () => void;
  hideActions?: boolean;
  selected?: boolean;
  /** Клик по телу карточки — переход в задачу. Если задан, имеет приоритет
   *  над onPause (используется для карточек-превью, которые просто открывают
   *  задачу, а не запускают её). */
  onOpen?: () => void;
}

export const ActiveTaskCard = ({
  task,
  elapsedMs,
  plannedMs,
  assignee,
  onPause,
  onOpen,
  selected = false,
  // onComplete,
  // createdBy,
  // previewImage,
  // hideActions,
}: ActiveTaskCardProps) => {
  const clickHandler = onOpen ?? onPause;
  const plannedMin = Math.max(0, Math.floor((plannedMs ?? 0) / 60000));
  const elapsedMin = Math.max(0, Math.floor((elapsedMs ?? 0) / 60000));

  // В API нет поля прогресса — берём долю затраченного времени как прокси.
  const progressPct =
    plannedMin > 0
      ? Math.min(100, Math.round((elapsedMin / plannedMin) * 100))
      : 0;

  const title = task?.title || 'Без названия';
  const projectTitle = getStringValue(task?.project?.Title) || 'Без проекта';
  const companyTitle = getStringValue(task?.company?.Title);
  const headerLine = task?.project?.Title
    ? `${projectTitle} / ${title}`
    : title;

  const dueText = safeFormatDate(task?.dueDate, 'dd.MM.yyyy');
  const daysLeft = getDaysLeft(task?.dueDate);
  const teamCount = Array.isArray(task?.accomplices)
    ? task.accomplices.length
    : 0;

  const assigneeName = getDisplayName(assignee) || 'Не назначен';

  // ===== новая карточка =====
  return (
    <div
      onClick={clickHandler}
      role={clickHandler ? 'button' : undefined}
      tabIndex={clickHandler ? 0 : undefined}
      className={`
        box-border w-full max-w-[469px] rounded-[16px]
        px-[8px] py-[14px]
        flex flex-col gap-[8px]
        bg-[#F8F7FC] shadow-[0px_3.54px_12.88px_rgba(0,0,0,0.03)]
        transition
        ${clickHandler ? 'cursor-pointer' : ''}
        ${
          selected
            ? 'ring-2 ring-[#57B792]'
            : 'ring-1 ring-transparent hover:ring-[#3B4D7F]/20'
        }
      `}
    >
      {/* Шапка-градиент: проект / название */}
      <div className="flex items-center px-[12px] py-[8px] rounded-[8px] bg-[linear-gradient(257.42deg,#152242_0.84%,#3B4D7F_100.65%)] shadow-[0px_3.54px_12.88px_rgba(0,0,0,0.1)]">
        <span className="truncate text-white text-[11px] font-medium leading-[130%] tracking-[-0.03em]">
          {headerLine}
        </span>
      </div>

      {/* Тело */}
      <div className="flex flex-col gap-[11px] px-[12px] py-[12px] rounded-[8px] bg-white shadow-[0px_3.54px_12.88px_rgba(0,0,0,0.03)]">
        {/* Срок + кольцо прогресса */}
        <div className="flex items-start justify-between gap-[12px]">
          <div className="flex flex-col gap-[10px] min-w-0">
            <div className="flex items-center gap-[4px]">
              <FlagIcon width={12} height={12} color="#57B792" />
              <span className="text-[#57B792] text-[10px] font-semibold leading-[12px] tracking-[-0.03em]">
                {dueText || '—'}
              </span>
            </div>
            <span className="text-[#8A8A8A] text-[10px] font-medium leading-[12px] tracking-[-0.03em]">
              Срок: {daysLeft != null ? `${daysLeft} дней` : '—'}
            </span>
          </div>

          <ProgressRing value={progressPct} />
        </div>

        {/* Проект / компания */}
        <div className="flex items-center justify-between gap-[12px]">
          <span className="truncate text-[#8A8A8A] text-[10px] font-medium leading-[12px] tracking-[-0.03em]">
            {projectTitle}
          </span>
          {companyTitle && (
            <span className="shrink-0 truncate text-[#8A8A8A] text-[10px] font-medium leading-[12px] tracking-[-0.03em]">
              {companyTitle}
            </span>
          )}
        </div>

        {/* Исполнитель / команда */}
        <div className="flex items-center justify-between gap-[12px]">
          <div className="flex items-center gap-[4px] min-w-0">
            {assignee ? (
              <UserAvatar user={assignee} size="xs" />
            ) : (
              <div className="w-[14px] h-[14px] rounded-full bg-gray-200" />
            )}
            <span className="truncate text-[#575757] text-[12px] font-medium leading-[15px] tracking-[-0.03em]">
              {assigneeName}
            </span>
          </div>

          <div className="flex items-center gap-[6px] text-[#575757] shrink-0">
            <span className="text-[12px] font-medium leading-[15px]">+</span>
            <Groups width={16} height={16} className="text-[#575757]" />
            <span className="text-[11px] font-normal leading-[14px] tracking-[-0.03em]">
              {teamCount}
            </span>
          </div>
        </div>
      </div>
    </div>
  );

  // ===== предыдущая карточка =====
  // return (
  //   <div className="bg-[#192A33] rounded-[4px] px-[10px] py-[9px] w-[195px] min-h-[263.45px]">
  //     <div className="flex items-center gap-1 mb-2">
  //       {timeStatus?.display && (
  //         <StatusCircle
  //           color={timeStatus.statusColor!}
  //           className="shrink-0 translate-y-[-1.5px]"
  //           height={12.18}
  //           width={12.18}
  //         />
  //       )}
  //       <div className="text-[12px] font-normal leading-[130%] tracking-[-0.5px]">
  //         {timeStatus?.status ?? 'Без статуса'}{' '}
  //         {safeFormatDate(task?.dueDate, 'dd.MM.yy HH:mm')}
  //       </div>
  //     </div>
  //
  //     <div className="text-[12px] font-normal leading-[130%] tracking-[-0.5px] mb-2 line-clamp-2">
  //       {task?.title || 'Без названия'}
  //     </div>
  //
  //     <div className="flex items-end">
  //       <div className="relative w-[53px] h-[60px] overflow-hidden shrink-0">
  //         {previewImage ? (
  //           <img
  //             src={previewImage.downloadUrl}
  //             alt={previewImage.name}
  //             className="w-full h-full object-contain"
  //           />
  //         ) : null}
  //       </div>
  //
  //       <div className="ml-auto flex items-center gap-[8px] text-white text-[10px] font-normal leading-none">
  //         <span className="flex items-center gap-[2px]">
  //           <VideoCountIcon />
  //           {stats?.videos}/3
  //         </span>
  //         <span className="flex items-center gap-[2px]">
  //           <ImagesCountIcon />+{stats?.images}
  //         </span>
  //       </div>
  //     </div>
  //
  //     <div className="h-px bg-white/10 my-2" />
  //
  //     <div className="flex items-center text-[12px] tracking-[-0.5px] font-normal mb-2">
  //       <span className="line-clamp-2">
  //         {getStringValue(task?.project?.Title) || 'Без проекта'}
  //       </span>
  //
  //       <div className="ml-auto flex items-center gap-[4px]">
  //         <UserAvatar size="xss" user={assignee} />
  //         <ChevronRightIcon className="w-[15px] h-[15px]" />
  //         <UserAvatar size="xss" user={createdBy} />
  //       </div>
  //     </div>
  //
  //     <div className="h-px bg-white/10 my-2" />
  //
  //     <div className="flex flex-col gap-[2px] text-[12px] tracking-[-0.5px] font-normal mb-2">
  //       <div className="flex items-center gap-[2.5px]">
  //         <SandClock stroke="white" />
  //         <span>
  //           {formatTimeHMS(elapsedMs)} / {formatTimeHMS(plannedMs)}
  //         </span>
  //       </div>
  //
  //       <div className="flex items-center gap-[2.5px]">
  //         <SandClock stroke="white" />
  //         <div>Продление: 2,5ч</div>
  //       </div>
  //     </div>
  //
  //     {!hideActions && (
  //       <div className="flex items-center justify-between">
  //         <Button
  //           className="text-white !text-[8.21px] w-[72.5px] tracking-[-0.34px] hover:bg-transparent hover:border-[#ffffff86] hover:text-[#ffffff86] px-[16px] py-[6px] font-semibold leading-[130%] border rounded-[68.39px]"
  //           variant="ghost"
  //           onClick={onComplete}
  //         >
  //           Завершить
  //         </Button>
  //
  //         <button
  //           onClick={onPause}
  //           className="flex items-center h-[24.5px] w-[24.5px] justify-center shrink-0"
  //         >
  //           <Pause height={24.45} width={24.45} />
  //         </button>
  //       </div>
  //     )}
  //   </div>
  // );
};

function ProgressRing({ value }: { value: number }) {
  const size = 40;
  const stroke = 4;
  const r = (size - stroke) / 2;
  const circumference = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(100, value));
  const offset = circumference * (1 - pct / 100);

  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="#EAEAEC"
          strokeWidth={stroke}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="#57B792"
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
        />
      </svg>
      <span className="absolute inset-0 flex items-center justify-center text-[#575757] text-[10px] font-semibold tracking-[-0.03em]">
        {pct}%
      </span>
    </div>
  );
}

function getDaysLeft(dueDate?: string | null): number | null {
  if (!dueDate) {
    return null;
  }
  const due = new Date(dueDate).getTime();
  if (Number.isNaN(due)) {
    return null;
  }
  return Math.ceil((due - Date.now()) / 86_400_000);
}
